import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AuthApiService } from "@core/services/auth-api.service";
import { LoggerService } from "@core/services/logger.service";
import { SettingStorageService } from "@core/services/storages/setting-storage.service";
import { DialogService, UtilityService } from "@databank-ui";
import { of, Subject } from "rxjs";
import { catchError, takeUntil, tap } from "rxjs/operators";
import { Router } from "@angular/router";
import { ResponseMessageService } from "../../services/response-message.service";
import { META_DATA } from "./login.metadata";
import { LoginFormConfig, LoginFormValue } from "./configs/login-form.config";

@Component({
    selector: "dbp-login",
    templateUrl: "./login.component.html",
    styleUrls: ["../../auth.module.scss", "./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
    public meta = META_DATA;
    public message = "";
    public loginForm: FormGroup<LoginFormConfig>;
    public defaultLoginFormValue: LoginFormValue;
    public isLoading = false;
    passwordCustomErrors = {
        pattern:
            "Should contain at least 1 digit, 1 special character and 1 uppercase and lowercase characters",
    };

    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private authApiService: AuthApiService,
        private settingStorageService: SettingStorageService,
        private responseMessageService: ResponseMessageService,
        private fb: FormBuilder,
        private dialog: DialogService,
        private router: Router,
        private loggerService: LoggerService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loggerService.log("[LoginComponent] ngOnInit");
        if (UtilityService.isIEBrowser()) {
            this.showBrowserAdviceModal();
        }

        const isRemembered = this.settingStorageService.checkIfRememberMeOn();
        if (isRemembered) {
            this.defaultLoginFormValue = {
                username: this.settingStorageService.getUserName(),
                remember: !!isRemembered,
            };
        }
        // Form creation
        this.loginForm = new FormGroup(new LoginFormConfig(this.defaultLoginFormValue));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // convenience getter for easy access to form fields
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    get f() {
        return this.loginForm.controls;
    }

    get hasMessage(): boolean {
        return !!this.message?.length;
    }

    updateRemember(value: boolean): void {
        this.settingStorageService.updateSetting({
            rememberMe: value,
        });
    }

    submit(): void {
        // show spinner
        this.isLoading = true;
        // clean up previous error message
        this.message = "";

        // send a request
        this.authApiService
            .simpleAuth(this.f.username.value, this.f.password.value)
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    // Update RememberMe Checkbox
                    this.updateRemember(this.f.remember.value);
                    this.router.navigate(["/home"]);
                }),
                catchError(err => {
                    // process the error and display the error text
                    this.isLoading = false;
                    this.message = err.errorContent;
                    this.cdr.markForCheck();
                    return of(err);
                })
            )
            .subscribe(() => {
                this.isLoading = false;
                this.cdr.markForCheck();
            });
    }

    private showBrowserAdviceModal() {
        this.dialog.confirm({
            data: {
                body: `For the best experience while using the DataBank Portal,<br>please use one of the following browsers:
                    <ul>
                    <li><strong>Google Chrome (click <a href='https://www.google.com/chrome/' target='_blank'>here</a> to navigate)</strong></li>
                    <li><strong>Microsoft Edge (click <a href='https://www.microsoft.com/en-us/edge' target='_blank'>here</a> to navigate)</strong></li>
                    </ul>`,
                title: "Browser recommendations",
                onConfirm: () => {},
                okButton: "Continue",
                hideCancelButton: true,
            },
        });
    }
}
